import { request as http } from '@/utils/http'

/**
 * 运价配比 API（/gateway/price/fcl/ratio/**）
 * 契约来源：docs/6a/模块一 后端运价配比/DESIGN_运价配比.md v5 §0.1/§10
 */

// ---------- 类型定义（对齐后端 RatioDraftVO/RatioBatchInfoVO/RatioTaskProgressVO） ----------

/** 配比源条目（弹窗一行 = 一个源合约/批次 + 权重） */
export interface RatioSourceItem {
  /** 源批次号（按合约配比时可选） */
  batchNo?: string
  /** 源合约号（按合约配比时必填，按批次配比时可选） */
  contractNo?: string
  /** 约价性质（1-FAK, 8-FIX；查无合约时手动指定） */
  pricingNature?: number
  /** 配比权重（1位小数，(0, 9.9]） */
  ratioWeight: number
  /** 卸货港航线ID（可选；非空时按此筛选源批次运价行） */
  podRouteId?: string | null
  /** 目的地ID列表（可选；非空时优先按此筛选源批次运价行，忽略 podRouteId） */
  pdls?: string[]
}

/** 草稿 VO（保存成功返回 draftId/draftVersion 供执行乐观锁） */
export interface RatioDraftVO {
  draftId?: string
  draftVersion?: number
  effectiveBegin?: string
  effectiveEnd?: string
  sources?: RatioSourceItem[]
}

/** 草稿保存请求 */
export interface RatioDraftSaveRequest {
  effectiveBegin: string
  effectiveEnd: string
  sources: RatioSourceItem[]
}

/** 执行请求 */
export interface RatioExecuteRequest {
  draftId: string
  draftVersion: number
  effectiveBegin: string
  effectiveEnd: string
  sources: RatioSourceItem[]
}

/** 批次/合约带出信息 */
export interface RatioBatchInfoVO {
  batchNo?: string
  contractNo?: string
  pricingNature?: 1 | 8
  natureText?: 'FAK' | 'Name Account (Fix)'
  priceBegin?: string
  priceEnd?: string
  priceRowCount?: number
  /** 组合在架弱提示（该批次合约参与的组合已有上架价） */
  comboOnShelfWarn?: boolean
  /** 合约表查无此合约（合约不存在） */
  contractNotFound?: boolean
  /** 合约表存在该合约但合约性质缺失/非 FAK/FIX */
  contractNatureMissing?: boolean
  /** 合约ID（若存在） */
  contractId?: string | number
  /** 批次内运价未关联合约（为 true 时允许前端用户手动搜索并选择合约） */
  contractEditable?: boolean
  /** 批次内运价涉及的唯一卸货港航线ID列表 */
  podRouteIds?: string[]
}

/** 任务进度（status: 1处理中/2已完成/3失败/4等待中/5已停止） */
export interface RatioTaskProgressVO {
  taskId: string
  status: number
  step?: string
  progress?: number
  sourceReadAt?: string
  finished?: boolean
  result?: {
    taskId?: string
    batchNo?: string
    ratioContractSet?: string
    sourceReadAt?: string
    matchedDimensionRowCount?: number
    matchedContainerCount?: number
    unmatchedDimensionRowCount?: number
    unmatchedContainerCount?: number
    insertedCount?: number
    unmatchedFileId?: string | null
    unmatchedFileName?: string | null
    warnReason?: string | null
    failReason?: string | null
    cleanupHint?: string | null
  }
}

// ---------- 接口 ----------

/** 获取当前用户草稿（无则 data 为 null） */
export const getDraft = () => {
  return http.get<RatioDraftVO | null>('/price/fcl/ratio/draft')
}

/** 保存当前用户草稿（upsert，返回含 draftId/draftVersion） */
export const saveDraft = (params: RatioDraftSaveRequest) => {
  return http.post<RatioDraftVO>('/price/fcl/ratio/draft', params)
}

/** 清空当前用户草稿 */
export const deleteDraft = () => {
  return http.delete<boolean>('/price/fcl/ratio/draft')
}

/** 批次/合约带出：单合约校验 + 约价性质/价格有效期 */
export const getBatchInfo = (batchNo?: string, contractNo?: string) => {
  return http.get<RatioBatchInfoVO>('/price/fcl/ratio/batch-info', {
    batchNo,
    contractNo
  })
}

/** 合约组合排他预检（事前弱提示）：同组合是否已有上架价 */
export const comboPrecheck = (ratioContractSet: string) => {
  return http.get<boolean>('/price/fcl/ratio/combo-precheck', { ratioContractSet })
}

/** 执行配比（乐观锁消费草稿 + 提交异步任务），返回 taskId（= 结果批次号） */
export const executeRatio = (params: RatioExecuteRequest) => {
  return http.post<string>('/price/fcl/ratio/execute', params)
}

/** 配比任务进度（前端 3s 轮询） */
export const getRatioTaskProgress = (taskId: string) => {
  return http.get<RatioTaskProgressVO>(`/price/fcl/ratio/task/${taskId}`)
}

/**
 * 未匹配明细导出（blob 下载）
 * 后端流式转发 storage 文件，前端以 arrayblob 接收
 */
export const exportUnmatched = (taskId: string) => {
  return http.get('/price/fcl/ratio/unmatched/export', { taskId }, {
    responseType: 'blob'
  })
}

